/**
 * Central API Service for CRM PHE
 * Handles all backend communication
 */

import axios from 'axios';
import { setGlobalCsrfToken } from '../utils/csrfFetch';

// Use environment variable or fallback to production URL
const API_BASE_URL = '/api/v1';

/**
 * Type-safe helper to convert an object with optional values to a URL query string.
 * Filters out undefined/null values and converts all values to strings.
 */
function toQueryString(params: Record<string, string | number | boolean | undefined | null>): string {
  const searchParams = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null) {
      searchParams.append(key, String(value));
    }
  }
  return searchParams.toString();
}

interface APIResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
}

class APIService {
  private baseURL: string;
  private csrfToken: string | null = null;

  constructor() {
    this.baseURL = API_BASE_URL;
    console.log('🔗 API Service initialized with baseURL:', this.baseURL);
  }

  /**
   * Fetch CSRF token from server
   * Should be called after login
   */
  async fetchCsrfToken(): Promise<void> {
    try {
      const response = await fetch(`${this.baseURL}/auth/csrf-token`, {
        method: 'GET',
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        if (data.success && data.csrfToken) {
          this.csrfToken = data.csrfToken;
          // Also set as global axios default so all axios-based services get the token
          axios.defaults.headers.common['X-CSRF-Token'] = data.csrfToken;
          // Und global für den window.fetch-Wrapper (rohe fetch-Aufrufe)
          setGlobalCsrfToken(data.csrfToken);
          console.log('🛡️ CSRF Token refreshed');
        }
      }
    } catch (error) {
      console.warn('Failed to fetch CSRF token:', error);
    }
  }

  /**
   * Clear CSRF token (call on logout)
   */
  clearCsrfToken(): void {
    this.csrfToken = null;
    delete axios.defaults.headers.common['X-CSRF-Token'];
    setGlobalCsrfToken(null);
  }

  /**
   * Generic fetch wrapper with error handling
   * Uses httpOnly cookies for authentication (credentials: 'include')
   */
  private async fetchAPI<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<APIResponse<T>> {
    try {
      const url = `${this.baseURL}${endpoint}`;

      const response = await fetch(url, {
        ...options,
        credentials: 'include', // ✅ Send httpOnly cookies automatically
        headers: {
          'Content-Type': 'application/json',
          ...(this.csrfToken && { 'X-CSRF-Token': this.csrfToken }), // ✅ Include CSRF token
          ...options.headers,
        },
      });

      // Handle 401 - Token expired, try to refresh
      if (response.status === 401) {
        const refreshed = await this.tryRefreshToken();
        if (refreshed) {
          // Also refresh CSRF token after auth refresh
          await this.fetchCsrfToken();
          // Retry original request with new token
          return this.fetchAPI(endpoint, options);
        }
        // Refresh failed - will be handled by AuthContext
      }

      // Handle 403 - CSRF token invalid, refresh and retry
      if (response.status === 403) {
        const data = await response.clone().json();
        if (data.code === 'INVALID_CSRF_TOKEN') {
          await this.fetchCsrfToken();
          // Retry original request with new CSRF token
          return this.fetchAPI(endpoint, options);
        }
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      return data;
    } catch (error: unknown) {
      console.error('API Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  /**
   * Try to refresh the access token using refresh token cookie
   */
  private async tryRefreshToken(): Promise<boolean> {
    try {
      const response = await fetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      return response.ok;
    } catch {
      return false;
    }
  }

  // ========================
  // DASHBOARD
  // ========================

  async getDashboardStats() {
    return this.fetchAPI('/dashboard/stats');
  }

  // ========================
  // CANDIDATES
  // ========================

  async getCandidates(params?: { limit?: number; offset?: number; status?: string; search?: string; stage?: string; filter?: string; letter?: string; sortBy?: string; sortOrder?: string }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/candidates${query ? `?${query}` : ''}`);
  }

  /**
   * Get ALL candidates with a single request (up to 10000)
   * Backend now uses .range() instead of .limit(), same as companies
   * No batch loading needed!
   */
  async getAllCandidates(): Promise<APIResponse<any[]>> {
    console.log('🔄 apiService: Loading ALL candidates with single request...');
    return this.getCandidates({ limit: 10000, offset: 0 }) as Promise<APIResponse<any[]>>;
  }

  async getCandidateById(id: string) {
    return this.fetchAPI(`/candidates/${id}`);
  }

  async createCandidate(candidateData: Record<string, unknown>) {
    return this.fetchAPI('/candidates', {
      method: 'POST',
      body: JSON.stringify(candidateData),
    });
  }

  async updateCandidate(id: string, updates: Record<string, unknown>) {
    return this.fetchAPI(`/candidates/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteCandidate(id: string) {
    return this.fetchAPI(`/candidates/${id}`, {
      method: 'DELETE',
    });
  }

  // ========================
  // CONTACTS
  // ========================

  async getContacts(params?: { limit?: number; offset?: number; search?: string; linked_company_id?: string }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/contacts${query ? `?${query}` : ''}`);
  }

  async getContactById(id: string) {
    return this.fetchAPI(`/contacts/${id}`);
  }

  async createContact(contactData: Record<string, unknown>) {
    return this.fetchAPI('/contacts', {
      method: 'POST',
      body: JSON.stringify(contactData),
    });
  }

  async updateContact(id: string, updates: Record<string, unknown>) {
    return this.fetchAPI(`/contacts/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteContact(id: string) {
    return this.fetchAPI(`/contacts/${id}`, {
      method: 'DELETE',
    });
  }

  // ========================
  // JOBS
  // ========================

  async getJobs(params?: { limit?: number; offset?: number; status?: string }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/jobs${query ? `?${query}` : ''}`);
  }

  async createJob(jobData: Record<string, unknown>) {
    return this.fetchAPI('/jobs', {
      method: 'POST',
      body: JSON.stringify(jobData),
    });
  }

  async updateJob(id: string, updates: Record<string, unknown>) {
    return this.fetchAPI(`/jobs/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteJob(id: string) {
    return this.fetchAPI(`/jobs/${id}`, {
      method: 'DELETE',
    });
  }

  // ========================
  // COMPANIES
  // ========================

  async getCompanies(params?: { page?: number; limit?: number; search?: string }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/companies${query ? `?${query}` : ''}`);
  }

  async getCompanyById(id: string) {
    return this.fetchAPI(`/companies/${id}`);
  }

  async createCompany(companyData: Record<string, unknown>) {
    return this.fetchAPI('/companies', {
      method: 'POST',
      body: JSON.stringify(companyData),
    });
  }

  async updateCompany(id: string, updates: Record<string, unknown>) {
    return this.fetchAPI(`/companies/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteCompany(id: string) {
    return this.fetchAPI(`/companies/${id}`, {
      method: 'DELETE',
    });
  }

  // ========================
  // TASKS
  // ========================

  async getTasks(params?: { status?: string }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/tasks${query ? `?${query}` : ''}`);
  }

  async createTask(taskData: Record<string, unknown>) {
    return this.fetchAPI('/tasks', {
      method: 'POST',
      body: JSON.stringify(taskData),
    });
  }

  async updateTask(id: string, updates: Record<string, unknown>) {
    return this.fetchAPI(`/tasks/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteTask(id: string) {
    return this.fetchAPI(`/tasks/${id}`, {
      method: 'DELETE',
    });
  }

  // ========================
  // DOCUMENTS
  // ========================

  async getDocuments(params?: { relatedTo?: string; relatedId?: string; limit?: number; search?: string }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/documents${query ? `?${query}` : ''}`);
  }

  async uploadDocument(formData: FormData) {
    const response = await fetch(`${this.baseURL}/documents/upload`, {
      method: 'POST',
      credentials: 'include', // ✅ Use cookies
      body: formData,
    });
    return response.json();
  }

  async updateDocument(id: string, updates: Record<string, unknown>) {
    return this.fetchAPI(`/documents/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    });
  }

  async deleteDocument(id: string) {
    return this.fetchAPI(`/documents/${id}`, {
      method: 'DELETE',
    });
  }

  // ========================
  // APPOINTMENTS
  // ========================

  async getAppointments(params?: { start?: string; end?: string }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/appointments${query ? `?${query}` : ''}`);
  }

  async createAppointment(appointmentData: Record<string, unknown>) {
    return this.fetchAPI('/appointments', {
      method: 'POST',
      body: JSON.stringify(appointmentData),
    });
  }

  async updateAppointment(id: string, updates: Record<string, unknown>) {
    return this.fetchAPI(`/appointments/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteAppointment(id: string) {
    return this.fetchAPI(`/appointments/${id}`, {
      method: 'DELETE',
    });
  }

  // ========================
  // ABSENCES
  // ========================

  async getAbsences(params?: { userId?: string; status?: string; startDate?: string; endDate?: string }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/absences${query ? `?${query}` : ''}`);
  }

  async getAbsenceStats() {
    return this.fetchAPI('/absences/stats');
  }

  async createAbsence(absenceData: Record<string, unknown>) {
    return this.fetchAPI('/absences', {
      method: 'POST',
      body: JSON.stringify(absenceData),
    });
  }

  async updateAbsence(id: string, updates: Record<string, unknown>) {
    return this.fetchAPI(`/absences/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async approveAbsence(id: string) {
    return this.fetchAPI(`/absences/${id}/approve`, {
      method: 'POST',
    });
  }

  async rejectAbsence(id: string, reason?: string) {
    return this.fetchAPI(`/absences/${id}/reject`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    });
  }

  async cancelAbsence(id: string) {
    return this.fetchAPI(`/absences/${id}/cancel`, {
      method: 'POST',
    });
  }

  async deleteAbsence(id: string) {
    return this.fetchAPI(`/absences/${id}`, {
      method: 'DELETE',
    });
  }

  // ========================
  // ACTIVITIES
  // ========================

  async getActivities(params?: { limit?: number; offset?: number; type?: string; companyId?: string }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/activities${query ? `?${query}` : ''}`);
  }

  async getActivityById(id: string, companyId?: string) {
    const query = companyId ? `?companyId=${companyId}` : '';
    return this.fetchAPI(`/activities/${id}${query}`);
  }

  async createActivity(activityData: Record<string, unknown>) {
    return this.fetchAPI('/activities', {
      method: 'POST',
      body: JSON.stringify(activityData),
    });
  }

  async createBulkActivities(activities: Record<string, unknown>[], companyId?: string) {
    return this.fetchAPI('/activities/bulk', {
      method: 'POST',
      body: JSON.stringify({ activities, companyId }),
    });
  }

  async getActivitiesByEntity(type: string, id: string, params?: { limit?: number; offset?: number; activityType?: string; includePinned?: boolean; companyId?: string }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/activities/entity/${type}/${id}${query ? `?${query}` : ''}`);
  }

  async getActivityStats(type: string, id: string, companyId?: string) {
    const query = companyId ? `?companyId=${companyId}` : '';
    return this.fetchAPI(`/activities/entity/${type}/${id}/stats${query}`);
  }

  async getActivityTimeline(type: string, id: string, companyId?: string) {
    const query = companyId ? `?companyId=${companyId}` : '';
    return this.fetchAPI(`/activities/timeline/${type}/${id}${query}`);
  }

  async searchActivities(query: string, params?: { companyId?: string; limit?: number }) {
    const searchParams = toQueryString({ q: query, ...params });
    return this.fetchAPI(`/activities/search?${searchParams}`);
  }

  async updateActivity(id: string, updates: Record<string, unknown>, companyId?: string) {
    return this.fetchAPI(`/activities/${id}`, {
      method: 'PUT',
      body: JSON.stringify({ ...updates, companyId }),
    });
  }

  async toggleActivityPin(id: string, companyId?: string) {
    return this.fetchAPI(`/activities/${id}/pin`, {
      method: 'PATCH',
      body: JSON.stringify({ companyId }),
    });
  }

  async deleteActivity(id: string, companyId?: string) {
    const query = companyId ? `?companyId=${companyId}` : '';
    return this.fetchAPI(`/activities/${id}${query}`, {
      method: 'DELETE',
    });
  }

  // ========================
  // NOTES
  // ========================

  async getNotes(params?: { companyId?: string; tag?: string; search?: string; limit?: number }) {
    const query = params ? toQueryString(params) : '';
    return this.fetchAPI(`/notes${query ? `?${query}` : ''}`);
  }

  async getNoteById(id: string, companyId?: string) {
    const query = companyId ? `?companyId=${companyId}` : '';
    return this.fetchAPI(`/notes/${id}${query}`);
  }

  async createNote(noteData: Record<string, unknown>, companyId?: string) {
    const query = companyId ? `?companyId=${companyId}` : '';
    return this.fetchAPI(`/notes${query}`, {
      method: 'POST',
      body: JSON.stringify(noteData),
    });
  }

  async updateNote(id: string, updates: Record<string, unknown>, companyId?: string) {
    const query = companyId ? `?companyId=${companyId}` : '';
    return this.fetchAPI(`/notes/${id}${query}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  async deleteNote(id: string, companyId?: string) {
    const query = companyId ? `?companyId=${companyId}` : '';
    return this.fetchAPI(`/notes/${id}${query}`, {
      method: 'DELETE',
    });
  }

  // ========================
  // AI - EMAIL
  // ========================

  async generateEmailDraft(params: {
    recipient?: string;
    subject?: string;
    purpose?: string;
    context?: string;
  }) {
    return this.fetchAPI('/ai/email/draft', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async generateCandidateEmail(params: {
    candidate: Record<string, unknown>;
    match: Record<string, unknown>;
    isAcceptance: boolean;
  }) {
    return this.fetchAPI('/ai/email/candidate', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async analyzeEmail(emailContent: string) {
    return this.fetchAPI('/ai/email/analyze', {
      method: 'POST',
      body: JSON.stringify({ emailContent }),
    });
  }

  // ========================
  // AI - INTERVIEW
  // ========================

  async generateInterviewQuestions(params: { candidate: Record<string, unknown>; match: Record<string, unknown> }) {
    return this.fetchAPI('/ai/interview/questions', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // ========================
  // AI - CANDIDATE
  // ========================

  async generateCandidateSummary(resume: Record<string, unknown>) {
    return this.fetchAPI('/ai/candidate/summary', {
      method: 'POST',
      body: JSON.stringify({ resume }),
    });
  }

  // ========================
  // AI - CV MATCHING
  // ========================

  async matchCV(params: {
    cvText: string;
    jobDescription: string;
    requiredSkills?: string[];
  }) {
    return this.fetchAPI('/ai/cv/match', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  async bulkMatchCVs(formData: FormData) {
    try {
      const url = `${this.baseURL}/ai/cv/bulk-match`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // ✅ Use cookies
        body: formData, // Don't set Content-Type for FormData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      return data;
    } catch (error: unknown) {
      console.error('Bulk CV Match Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Network error',
      };
    }
  }

  // ========================
  // AI - JOB DESCRIPTION
  // ========================

  async generateJobDescription(params: {
    title: string;
    bulletPoints?: string[];
    department?: string;
    location?: string;
  }) {
    return this.fetchAPI('/ai/job/description', {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }

  // ========================
  // PDF GENERATION
  // ========================

  async generateContractPDF(contractData: Record<string, unknown>): Promise<Blob> {
    try {
      const url = `${this.baseURL}/pdf/contract`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // ✅ Use cookies
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(contractData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.blob();
    } catch (error: unknown) {
      console.error('PDF Generation Error:', error);
      throw error;
    }
  }

  async generateCandidateProfilePDF(candidateData: Record<string, unknown>): Promise<Blob> {
    try {
      const url = `${this.baseURL}/pdf/candidate-profile`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // ✅ Use cookies
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(candidateData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.blob();
    } catch (error: unknown) {
      console.error('PDF Generation Error:', error);
      throw error;
    }
  }

  async generateJobDescriptionPDF(jobData: Record<string, unknown>): Promise<Blob> {
    try {
      const url = `${this.baseURL}/pdf/job-description`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // ✅ Use cookies
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jobData),
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }

      return await response.blob();
    } catch (error: unknown) {
      console.error('PDF Generation Error:', error);
      throw error;
    }
  }

  // ========================
  // FILE UPLOAD
  // ========================

  async uploadFile(file: File, bucket?: string, path?: string) {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (bucket) formData.append('bucket', bucket);
      if (path) formData.append('path', path);

      const url = `${this.baseURL}/upload`;

      const response = await fetch(url, {
        method: 'POST',
        credentials: 'include', // ✅ Use cookies
        body: formData,
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }

      return data;
    } catch (error: unknown) {
      console.error('File Upload Error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Upload failed',
      };
    }
  }

  // ========================
  // CHAT AI
  // ========================

  async sendChatMessage(message: string, companyId: string, userId: string) {
    return this.fetchAPI('/chat/message', {
      method: 'POST',
      body: JSON.stringify({ message, companyId, userId }),
    });
  }

  async getChatHistory(companyId: string, userId: string, limit = 50) {
    return this.fetchAPI(
      `/chat/history?companyId=${companyId}&userId=${userId}&limit=${limit}`
    );
  }

  // ========================
  // TIME TRACKING
  // ========================

  async checkIn(data: { location?: string; projectId?: string; notes?: string }) {
    return this.fetchAPI('/time-tracking/check-in', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async checkOut(data?: { notes?: string }) {
    return this.fetchAPI('/time-tracking/check-out', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async startBreak(data?: { breakType?: string; notes?: string }) {
    return this.fetchAPI('/time-tracking/start-break', {
      method: 'POST',
      body: JSON.stringify(data || {}),
    });
  }

  async endBreak() {
    return this.fetchAPI('/time-tracking/end-break', {
      method: 'POST',
    });
  }

  async getTodayTimeEntry() {
    return this.fetchAPI('/time-tracking/today');
  }

  async getMyTimeEntries(params: { startDate: string; endDate: string }) {
    const query = new URLSearchParams(params).toString();
    return this.fetchAPI(`/time-tracking/my-entries?${query}`);
  }

  async getTeamOverview(date: string) {
    return this.fetchAPI(`/time-tracking/team?date=${date}`);
  }

  async getEmployeeTimeEntries(userId: string, params: { startDate: string; endDate: string }) {
    const query = new URLSearchParams(params).toString();
    return this.fetchAPI(`/time-tracking/employee/${userId}?${query}`);
  }

  // ========================
  // HEALTH CHECK
  // ========================

  async healthCheck() {
    return this.fetchAPI('/health');
  }
}

// Export singleton instance
export const apiService = new APIService();
export default apiService;
